import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Check, ArrowUp, ArrowDown, Upload, ClipboardPaste, FileText } from 'lucide-react';
import { formatCurrency } from '../../utils/formatting';
import { resolveCategoryName, computeBalanceCostBasis } from '../../utils/calculations';
import { SYMBOL_TO_CODE } from '../../utils/constants';
import { getCurrencyInfo, getAllCurrencies } from '../../utils/currencies';
import { parseWorkbook } from '../../utils/excelImport';
import { SearchableSelect } from '../Common/Common';
import ExcelMappingStep from './ExcelMappingStep';

/* ─── Parse helpers ─── */
function parseDate(str) {
  const m = str.trim().match(/^(\d{1,2})[.\/\-](\d{1,2})[.\/\-](\d{2,4})$/);
  if (!m) return null;
  const [, dd, mm, yy] = m;
  const year = yy.length === 2 ? 2000 + parseInt(yy) : parseInt(yy);
  return `${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

const CURRENCY_PATTERN = /^[₪$€£¥]$|^Fr$|^CA\$$|^A\$$|^[A-Z]{2,4}$/;

function parseLines(text, importCurrency = 'ILS') {
  return text
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .map(line => {
      const parts = line.split('\t').map(s => s.trim());
      if (parts.length < 3) return null;
      const date = parseDate(parts[0]);
      if (!date) return null;

      // Detect optional currency column (last column if it looks like a symbol/code)
      let currency = importCurrency;
      let amountIdx = parts.length - 1;
      const lastPart = parts[parts.length - 1];
      if (CURRENCY_PATTERN.test(lastPart)) {
        // Last column is a currency symbol/code
        const code = SYMBOL_TO_CODE[lastPart] || (lastPart.length <= 4 ? lastPart : importCurrency);
        currency = code;
        amountIdx = parts.length - 2;
      }
      if (amountIdx < 1) return null;

      const amountRaw = parts[amountIdx].replace(/,(?=\d{3})/g, '').replace(',', '.');
      const amount = parseFloat(amountRaw);
      if (isNaN(amount) || amount === 0) return null;

      const description = parts.slice(1, amountIdx).join(' ').trim();
      return {
        date,
        description,
        amount: Math.abs(amount),
        type: 'expense',   // default to expense; user can toggle per row
        currency,
        exchangeRate: '',  // user fills in categorize step for foreign currencies
        categoryId: null,
      };
    })
    .filter(Boolean);
}

/* ─── Main component ─── */
export default function PasteImport({ categories, yearlyExpenses = [], addYearlyExpense, catIndex = {}, settings, currencyBalances, onAdd, onClose }) {
  const mainCurrency = settings.currency || 'ILS';
  const [importCurrency, setImportCurrency] = useState(mainCurrency);
  const [step, setStep] = useState('file');
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [excelSheets, setExcelSheets] = useState(null);
  const [excelError, setExcelError] = useState('');
  const listRef = useRef(null);
  const activeRowRef = useRef(null);
  const fileInputRef = useRef(null);

  async function handleFileUpload(file) {
    setExcelError('');
    if (!file) return;
    const filename = file.name.toLowerCase();
    if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
      try {
        const buf = await file.arrayBuffer();
        const sheets = parseWorkbook(buf);
        if (!sheets.length) { setExcelError('No sheets found in workbook.'); return; }
        setExcelSheets(sheets);
        setStep('excel-map');
      } catch (err) {
        setExcelError('Failed to parse Excel file — is it a valid .xlsx?');
      }
    } else {
      // Text or CSV file
      try {
        const fileContent = await file.text();
        setText(fileContent);
        const parsed = parseLines(fileContent, importCurrency);
        if (!parsed.length) {
          setExcelError('Could not parse any transaction rows from this file.');
          return;
        }
        setRows(parsed);
        setActiveIdx(0);
        setStep('categorize');
      } catch (err) {
        setExcelError('Failed to read file contents.');
      }
    }
  }

  function handleExcelConfirm(parsedRows) {
    if (!parsedRows.length) return;
    // Ensure rows default to importCurrency if not explicitly set
    const updatedRows = parsedRows.map(r => ({
      ...r,
      currency: r.currency || importCurrency,
    }));
    setRows(updatedRows);
    setActiveIdx(0);
    setExcelSheets(null);
    setStep('categorize');
  }

  // Freeze the ranking index at open time — rankings must not shift while the user is working
  const frozenCatIndex = useRef(catIndex);
  const fmt = v => formatCurrency(v, settings);
  const [newYearlyName, setNewYearlyName] = useState('');

  const expenseCats = categories.filter(c => c.type === 'expense');
  const incomeCats  = categories.filter(c => c.type === 'income');

  function catsFor(type) { return type === 'income' ? incomeCats : expenseCats; }

  // All options for a type (flat list with isYearly flag)
  function allOptionsFor(type) {
    const base = catsFor(type);
    if (type !== 'expense') return base.map(c => ({ ...c, isYearly: false }));
    return [
      ...base.map(c => ({ ...c, isYearly: false })),
      ...yearlyExpenses.map(y => ({ ...y, isYearly: true })),
    ];
  }

  // Fixed ordered list per type — computed once at mount from the frozen index.
  function buildOrderedOpts(type) {
    const all     = allOptionsFor(type);
    const ranked  = frozenCatIndex.current[`${type}:`] || [];
    const rankedIds = ranked.map(r => r.catId);
    const byId    = Object.fromEntries(all.map(o => [o.id, o]));
    const ordered = rankedIds.filter(id => byId[id]).map(id => byId[id]);
    const rest    = all.filter(o => !rankedIds.includes(o.id));
    return [...ordered, ...rest];
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orderedExpenseOpts = React.useMemo(() => buildOrderedOpts('expense'), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orderedIncomeOpts  = React.useMemo(() => buildOrderedOpts('income'),  []);

  function orderedOptsFor(row) {
    return row?.type === 'income' ? orderedIncomeOpts : orderedExpenseOpts;
  }

  const parsedCount = text.trim() ? parseLines(text, importCurrency).length : 0;
  const categorizedCount = rows.filter(r => r.categoryId).length;

  function goToParse() { setStep('file'); }

  function handleParse() {
    const parsed = parseLines(text, importCurrency);
    if (!parsed.length) return;
    setRows(parsed);
    setActiveIdx(0);
    setStep('categorize');
  }

  const assignCategory = useCallback((idx, catId) => {
    setRows(prev => {
      const next = prev.map((r, i) => i === idx ? { ...r, categoryId: catId } : r);
      // Advance to next uncategorized
      const nextUncategorized = next.findIndex((r, i) => i > idx && !r.categoryId);
      if (nextUncategorized >= 0) setActiveIdx(nextUncategorized);
      else {
        const anyUncategorized = next.findIndex(r => !r.categoryId);
        if (anyUncategorized >= 0) setActiveIdx(anyUncategorized);
        else setActiveIdx(Math.min(idx, next.length - 1));
      }
      return next;
    });
  }, []);

  function toggleType(idx) {
    setRows(prev => prev.map((r, i) => i === idx
      ? { ...r, type: r.type === 'expense' ? 'income' : 'expense', categoryId: null }
      : r
    ));
  }

  function updateRate(idx, rate) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, exchangeRate: rate } : r));
  }

  function handleAdd() {
    const toAdd = rows
      .filter(r => r.categoryId)
      .map(r => {
        const isForeign = r.currency !== mainCurrency;
        // Use average cost basis from matching currency balance if no explicit rate given
        const matchBal = currencyBalances?.find(b => b.currency === r.currency);
        const { avgRate } = matchBal ? computeBalanceCostBasis(matchBal) : { avgRate: 1 };
        const rate = isForeign ? (parseFloat(r.exchangeRate) || avgRate || 1) : 1;
        const nisAmount = isForeign ? parseFloat((r.amount * rate).toFixed(2)) : r.amount;
        return {
          date: r.date, description: r.description,
          amount: nisAmount, type: r.type, category: r.categoryId, notes: '', bucket: null,
          currency: r.currency,
          foreignAmount: isForeign ? r.amount : null,
          exchangeRate: isForeign ? rate : null,
          fromBalance: isForeign && !!matchBal,
          currencyBalanceId: isForeign && matchBal ? matchBal.id : null,
        };
      });
    onAdd(toAdd);
    onClose();
  }

  /* Keyboard navigation in categorize step */
  useEffect(() => {
    if (step !== 'categorize') return;
    function onKey(e) {
      if (e.target.tagName === 'TEXTAREA') return;
      e.stopImmediatePropagation();

      if (e.key === 'Escape') { onClose(); return; }

      if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, rows.length - 1));
        return;
      }
      if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        setRows(prev => prev.map((r, i) => i === activeIdx ? { ...r, categoryId: null } : r));
        return;
      }
      const num = parseInt(e.key);
      if (!isNaN(num) && num >= 1) {
        const opts = rows[activeIdx] ? orderedOptsFor(rows[activeIdx]) : [];
        const opt = opts[num - 1];
        if (opt) { e.preventDefault(); assignCategory(activeIdx, opt.id); }
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [step, activeIdx, rows, assignCategory, onClose]);

  /* Scroll active row into view */
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeIdx]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: 'var(--bg-primary)', borderRadius: 14, width: '100%', maxWidth: 700,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {(step === 'categorize' || step === 'excel-map' || step === 'paste') && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={goToParse} title="Back">
              <ChevronLeft size={15} />
            </button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {(step === 'file' || step === 'paste') ? 'Import bank statement'
                : step === 'excel-map' ? 'Map Excel columns'
                : 'Categorize transactions'}
            </div>
            {step === 'categorize' && (
              <div className="muted" style={{ fontSize: 12 }}>
                {categorizedCount}/{rows.length} categorized
                &ensp;·&ensp;↑↓ navigate
                &ensp;·&ensp;1–9 assign
                &ensp;·&ensp;⌫ clear
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>

        {/* ── STEP 1: Default File Upload Screen ── */}
        {step === 'file' && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, flex: 1, overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, fontWeight: 500, minWidth: 130 }}>Statement currency:</label>
              <div style={{ width: 140 }}>
                <SearchableSelect
                  value={importCurrency}
                  onChange={code => setImportCurrency(code)}
                  options={getAllCurrencies().map(c => ({
                    value: c.code,
                    label: c.code,
                    secondary: c.symbol,
                  }))}
                  placeholder="Currency…"
                />
              </div>
            </div>

            {excelError && (
              <div style={{ padding: '8px 12px', background: 'var(--expense-bg, #fee2e2)', color: 'var(--expense)', borderRadius: 8, fontSize: 13 }}>
                {excelError}
              </div>
            )}

            {/* Dropzone */}
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) handleFileUpload(file);
              }}
              style={{
                border: '2px dashed var(--border)',
                borderRadius: 12,
                padding: '36px 20px',
                textAlign: 'center',
                background: 'var(--bg-secondary)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={36} style={{ color: 'var(--savings)', opacity: 0.8 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Select a statement file to import</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  Supports Excel (.xlsx, .xls) and text/CSV (.csv, .txt) files
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.txt,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={e => { handleFileUpload(e.target.files?.[0]); e.target.value = ''; }}
              />
              <button className="btn btn-primary btn-sm" style={{ marginTop: 4 }}>
                <Upload size={14} /> Choose file
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 'auto' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setStep('paste')}>
                <ClipboardPaste size={14} /> Paste text instead
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── STEP 1.5: Paste Text Screen ── */}
        {step === 'paste' && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, fontWeight: 500, minWidth: 130 }}>Statement currency:</label>
              <div style={{ width: 140 }}>
                <SearchableSelect
                  value={importCurrency}
                  onChange={code => setImportCurrency(code)}
                  options={getAllCurrencies().map(c => ({
                    value: c.code,
                    label: c.code,
                    secondary: c.symbol,
                  }))}
                  placeholder="Currency…"
                />
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setStep('file')}>
                <FileText size={14} /> Upload file instead
              </button>
            </div>

            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              Paste tab-separated rows. Format: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>DD.MM.YY → merchant → amount</span>.
            </p>

            <textarea
              autoFocus
              role="textbox"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={"27.02.26\tמסעדת אל-דיאר בעמ\t47.00\n26.02.26\tבר 55\t30.00\n..."}
              style={{
                flex: 1, minHeight: 220, resize: 'none',
                fontFamily: 'var(--font-mono)', fontSize: 12,
                direction: 'ltr',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="muted" style={{ fontSize: 12, flex: 1 }}>
                {parsedCount > 0
                  ? <><strong style={{ color: 'var(--text-primary)' }}>{parsedCount}</strong> rows parsed</>
                  : text.trim() ? 'Could not parse any rows — check format' : ''}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleParse}
                disabled={parsedCount === 0}
              >
                Categorize {parsedCount > 0 ? parsedCount : ''} rows <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 1.8: Excel column mapping ── */}
        {step === 'excel-map' && excelSheets && (
          <ExcelMappingStep
            sheets={excelSheets}
            mainCurrency={importCurrency}
            onCancel={() => { setExcelSheets(null); setStep('file'); }}
            onConfirm={handleExcelConfirm}
          />
        )}

        {/* ── STEP 2: Categorize ── */}
        {step === 'categorize' && (
          <>
            {/* Transaction list */}
            <div ref={listRef} style={{ flex: 1, overflowY: 'auto' }}>
              {rows.map((row, i) => {
                const isActive = i === activeIdx;
                const catName = row.categoryId ? resolveCategoryName(row.categoryId, categories, yearlyExpenses) : null;
                return (
                  <div
                    key={i}
                    ref={isActive ? activeRowRef : null}
                    onClick={() => setActiveIdx(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 20px',
                      cursor: 'pointer',
                      background: isActive ? 'var(--bg-secondary)' : 'transparent',
                      borderLeft: `3px solid ${isActive ? 'var(--savings)' : 'transparent'}`,
                      transition: 'background 0.1s',
                    }}
                  >
                    {/* Row number */}
                    <span className="muted mono" style={{ fontSize: 11, minWidth: 20, textAlign: 'right' }}>{i + 1}</span>

                    {/* Date */}
                    <span className="muted mono" style={{ fontSize: 12, minWidth: 40 }}>
                      {row.date.slice(5).replace('-', '/')}
                    </span>

                    {/* Amount */}
                    <span className="mono" style={{
                      fontSize: 13, minWidth: 72, textAlign: 'right', flexShrink: 0,
                      color: row.type === 'income' ? 'var(--income)' : 'var(--expense)',
                    }}>
                      {row.type === 'income' ? '+' : '−'}
                      {row.currency !== mainCurrency
                        ? `${getCurrencyInfo(row.currency)?.symbol||row.currency}${row.amount.toFixed(2)}`
                        : fmt(row.amount)}
                    </span>

                    {/* Description (RTL) */}
                    <span style={{
                      flex: 1, fontSize: 13, direction: 'rtl', textAlign: 'right',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.description}
                    </span>

                    {/* Type toggle */}
                    <button
                      onClick={e => { e.stopPropagation(); toggleType(i); }}
                      style={{
                        flexShrink: 0, fontSize: 10, padding: '2px 6px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'transparent',
                        cursor: 'pointer', color: 'var(--text-tertiary)',
                      }}
                      title="Toggle income/expense"
                    >
                      {row.type === 'income' ? 'income' : 'expense'}
                    </button>

                    {/* Category badge */}
                    <div style={{ minWidth: 120, textAlign: 'right', flexShrink: 0 }}>
                      {catName ? (
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: 'var(--income-bg)', color: 'var(--income)', fontWeight: 500,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          <Check size={10} /> {catName}
                        </span>
                      ) : (
                        <span className="muted" style={{ fontSize: 11, fontStyle: 'italic' }}>unassigned</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Category picker */}
            <div style={{ borderTop: '1px solid var(--border)', padding: '12px 20px 16px', flexShrink: 0, background: 'var(--bg-secondary)' }}>
              {/* Active row summary */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-icon btn-xs" onClick={() => setActiveIdx(i => Math.max(i-1, 0))} title="Previous (↑)"><ArrowUp size={12} /></button>
                  <button className="btn btn-ghost btn-icon btn-xs" onClick={() => setActiveIdx(i => Math.min(i+1, rows.length-1))} title="Next (↓)"><ArrowDown size={12} /></button>
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, direction: 'rtl', flex: 1 }}>{rows[activeIdx]?.description}</span>
                {rows[activeIdx]?.currency !== mainCurrency && (() => {
                  const fc = getCurrencyInfo(rows[activeIdx].currency);
                  const rate = parseFloat(rows[activeIdx].exchangeRate) || 0;
                  const nis = rate > 0 ? rows[activeIdx].amount * rate : null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="muted" style={{ fontSize: 11 }}>{fc?.symbol||rows[activeIdx].currency} rate:</span>
                      <input
                        type="number" placeholder="NIS/unit" min="0" step="0.01"
                        value={rows[activeIdx].exchangeRate}
                        onChange={e => updateRate(activeIdx, e.target.value)}
                        style={{ width: 80, fontFamily: 'var(--font-mono)', fontSize: 12, padding: '3px 6px' }}
                      />
                      {nis !== null && <span className="mono income-text" style={{ fontSize: 12 }}>= {fmt(nis)}</span>}
                    </div>
                  );
                })()}
                <span className="muted mono" style={{ fontSize: 12 }}>
                  {rows[activeIdx]?.currency !== mainCurrency
                    ? `${getCurrencyInfo(rows[activeIdx]?.currency)?.symbol||''}${rows[activeIdx]?.amount}`
                    : fmt(rows[activeIdx]?.amount)}
                </span>
              </div>

              {/* Category chips */}
              {(() => {
                const activeRow  = rows[activeIdx];
                const activeId   = activeRow?.categoryId;
                const ordered    = activeRow ? orderedOptsFor(activeRow) : [];

                // Shortcut map: catId → 1-based key (first 9 in ranked order)
                const shortcutMap = {};
                ordered.forEach((opt, i) => { if (i < 9) shortcutMap[opt.id] = i + 1; });

                const regularOpts = ordered.filter(o => !o.isYearly);
                const yearlyOpts  = ordered.filter(o => o.isYearly);

                const Chip = ({ opt }) => {
                  const isAssigned = activeId === opt.id;
                  const shortcut   = shortcutMap[opt.id] ?? null;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => assignCategory(activeIdx, opt.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                        border: `1.5px solid ${isAssigned ? 'var(--income)' : 'var(--border)'}`,
                        background: isAssigned ? 'var(--income-bg)' : 'var(--bg-primary)',
                        color: isAssigned ? 'var(--income)' : 'var(--text-primary)',
                        fontWeight: isAssigned ? 600 : 400,
                        transition: 'all 0.1s',
                        boxShadow: isAssigned ? '0 0 0 3px var(--income-bg)' : 'none',
                      }}
                    >
                      {shortcut !== null && (
                        <span style={{
                          fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600,
                          background: isAssigned ? 'var(--income-bg)' : 'var(--bg-tertiary)',
                          color: isAssigned ? 'var(--income)' : 'var(--text-tertiary)',
                          borderRadius: 4, padding: '1px 4px', lineHeight: 1.4,
                        }}>
                          {shortcut}
                        </span>
                      )}
                      {opt.isYearly ? `📅 ${opt.name}` : opt.name}
                    </button>
                  );
                };

                return (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {regularOpts.map(opt => <Chip key={opt.id} opt={opt} />)}
                    </div>
                    {yearlyOpts.length > 0 && (
                      <>
                        <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '8px 0 5px' }}>
                          Yearly expenses
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {yearlyOpts.map(opt => <Chip key={opt.id} opt={opt} />)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Create yearly expense */}
              {addYearlyExpense && rows[activeIdx]?.type === 'expense' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--bg-tertiary)', border: '1px dashed var(--border)' }}>
                  <span className="muted" style={{ fontSize: 12, flexShrink: 0 }}>+ Yearly:</span>
                  <input
                    value={newYearlyName}
                    onChange={e => setNewYearlyName(e.target.value)}
                    placeholder="Enter name…"
                    style={{ flex: 1, fontSize: 12, padding: '3px 8px' }}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === 'Enter' && newYearlyName.trim()) {
                        const id = addYearlyExpense({ name: newYearlyName.trim(), amount: 0, month: null, frequency: 'annual', notes: '' });
                        assignCategory(activeIdx, id);
                        setNewYearlyName('');
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!newYearlyName.trim()}
                    onClick={() => {
                      const id = addYearlyExpense({ name: newYearlyName.trim(), amount: 0, month: null, frequency: 'annual', notes: '' });
                      assignCategory(activeIdx, id);
                      setNewYearlyName('');
                    }}
                  >Add</button>
                </div>
              )}

              {/* Footer */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="muted" style={{ fontSize: 12, flex: 1 }}>
                  {categorizedCount === rows.length
                    ? 'All categorized — ready to add!'
                    : `${rows.length - categorizedCount} unassigned rows will be skipped`}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
                <button
                  className="btn btn-primary"
                  disabled={categorizedCount === 0}
                  onClick={handleAdd}
                >
                  <Check size={14} /> Add {categorizedCount} transaction{categorizedCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
